import React, { useContext, useEffect, useState } from 'react';
import './FSapp.css';
import { K1StatusByFundEstimatedChart, K1StatusByFundFinalizedChart } from './reporting-widgets/K1TrackerCharts';
import { FormStatusByEntityChart, Deliverables_Status_Before_915, FundStatusChart, Deliverables_Status_after_915, StatusbyDueDate } from './reporting-widgets/DeliverableCharts';
import { IRStatusChart } from './reporting-widgets/IRCharts';
import { ESignatureStatusChart } from './reporting-widgets/ESignatureCharts';
import { Button as DxButton } from "devextreme-react/button";
import { getLastRefreshTimeFromRawData, convertUTCTimeToLocal } from './FSServices';
import useSnackbar from "../../hooks/useSnackbar";
import { SnackbarMessage } from '../../hooks/useSnackbar.types';
import { createSitesClientApi } from '../../api-client-nswag/taxportal-client-runtime';
import { OidcIdentityContext } from '@rsmus/react-auth';
import { HQISheetName } from '../../constants';
import { Box, CircularProgress } from '@mui/material';

export interface FundDataItem {
    fund: string;
    entityName: string;
    estimatedYes: number;
    estimatedNo: number;
    finalizedYes: number;
    finalizedNo: number;
    trueUpYes: number;
    trueUpNo: number;
    LastModified?: string;
}


type DashboardDataState = {
    deliverables: any[];
    k1Tracker: FundDataItem[];
    ir: any[];
    eSignature: any[];
};

type DashboardDataKey = keyof DashboardDataState;


function App() {
    let siteID = parseInt(sessionStorage.getItem("siteId")!) || 0;
    const { user } = useContext(OidcIdentityContext);
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState("");
    const [fsSiteFlag, setFsSiteFlag] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        deliverables: [],
        k1Tracker: [],
        ir: [],
        eSignature: []
    });



    useEffect(() => {
        setIsLoading(true);
        if (siteID == null) {
            return;
        }
        if (siteID === 0) {
            fetchAllDashboardData(true);
            setFsSiteFlag(true);
            return;
        }
        const fetchData = async () => {

            const response = await createSitesClientApi.taxportalDbData_GetCreateSiteTrackDetails(
                null,
                siteID,
                null
            );
            const fsSiteFlag = response.data?.some(site =>
                site.templateName?.toLowerCase().includes("fs")
            ) ?? false;

            setFsSiteFlag(fsSiteFlag);
            fetchAllDashboardData(fsSiteFlag);
        }
        fetchData();


    }, [siteID]);

    useEffect(() => {
        const fetchItemTrack = async () => {
            const itemTrack = await createSitesClientApi.taxportalDbData_GetItemSyncTrackByType(siteID, "SYNC_DELIVERABLES", user?.profile.preferred_username as string);
            const lastModified = (itemTrack as any)?.data?.lastModified ?? (itemTrack as any)?.lastModified
            const localTime = lastModified?.trim() ? convertUTCTimeToLocal(lastModified): '';
            setLastRefreshTime(localTime);
        };
        fetchItemTrack();
    }, [dashboardData.deliverables]);

    const k1LastRefreshTime = getLastRefreshTimeFromRawData(dashboardData.k1Tracker);

    const fetchAllDashboardData = async (fsSiteFlag: boolean): Promise<void> => {
        setIsLoading(true);

        try {
            const requests = [
                fetchDashboardData("ir"),
                fetchDashboardData("eSignature"),
                fetchDashboardData("k1Tracker")
            ];

            if (fsSiteFlag) {
                requests.unshift(
                    fetchDashboardData("deliverables"),
                );
            }

            await Promise.allSettled(requests);
        } finally {
            setIsLoading(false);
        }
    };

    const transformK1TrackerData = (items: any[]): FundDataItem[] => {
        const grouped: Record<string, FundDataItem> = {};

        items.forEach((x: any) => {
            if (!grouped[x.fund]) {
                grouped[x.fund] = {
                    fund: x.fund,
                    entityName: x.entity,
                    estimatedYes: 0,
                    estimatedNo: 0,
                    finalizedYes: 0,
                    finalizedNo: 0,
                    trueUpYes: 0,
                    trueUpNo: 0,
                    LastModified: x.LastModified || x.lastModified,
                };
            }

            const existingLastModified = grouped[x.fund].LastModified;
            const incomingLastModified = x.LastModified || x.lastModified;
            if (incomingLastModified) {
                if (!existingLastModified || new Date(incomingLastModified) > new Date(existingLastModified)) {
                    grouped[x.fund].LastModified = incomingLastModified;
                }
            }

            if (x.federalAndStateK1EstimatesReceived === 'Yes') {
                grouped[x.fund].estimatedYes += 1;
            } else {
                grouped[x.fund].estimatedNo += 1;
            }

            if (x.federalAndStateFinalizedK1Received === 'Yes') {
                grouped[x.fund].finalizedYes += 1;
            } else {
                grouped[x.fund].finalizedNo += 1;
            }

            if (x.priorYearFinalTrueUpK1Received === 'Yes') {
                grouped[x.fund].trueUpYes += 1;
            } else {
                grouped[x.fund].trueUpNo += 1;
            }
        });

        return Object.values(grouped);
    };

    const dashboardConfig: Record<
        DashboardDataKey,
        {
            sheetName: string;
            errorMessage: string;
            transform?: (data: any[]) => any[];
        }
    > = {
        deliverables: {
            sheetName: HQISheetName.DELIVERABLES,
            errorMessage: SnackbarMessage.FAILED_TO_SYNC_Deliverables,
        },
        k1Tracker: {
            sheetName: HQISheetName.K1_TRACKER,
            errorMessage: SnackbarMessage.FAILED_TO_SYNC_K1_TRACKER,
            transform: transformK1TrackerData,
        },
        ir: {
            sheetName: HQISheetName.INFORMATION_REQUEST,
            errorMessage: SnackbarMessage.FAILED_TO_SYNC_INFORMATION_REQUEST_LIST,
        },
        eSignature: {
            sheetName: HQISheetName.DOCUMENT_METADATA,
            errorMessage: SnackbarMessage.FAILED_TO_SYNC_Document_Metadata,
        },
    };

    const fetchDashboardData = async (key: DashboardDataKey): Promise<void> => {
        try {
            const config = dashboardConfig[key];

            const response = await createSitesClientApi.dashboard_GetISheetItemsByType(
                siteID,
                config.sheetName,
                user?.profile.preferred_username as string
            );

            const data = Array.isArray(response) ? response : [];

            const finalData = config.transform
                ? config.transform(data)
                : data;

            setDashboardData(prev => ({
                ...prev,
                [key]: finalData,
            }));
        } catch (error) {
            console.error(`Error fetching ${key} data:`, error);

            setDashboardData(prev => ({
                ...prev,
                [key]: [],
            }));
        }
    };

    const refreshISheetData = async (
        sheetName: string,
        errorMessage: string,
        successMessage: string,
        key: DashboardDataKey,
    ) => {
        try {
            const syncResponse = await createSitesClientApi.syncisheetitems_ISheetName(
                siteID,
                sheetName,
                user?.profile.preferred_username as string
            );

            // Sync API now returns an operation payload object, not the chart data array.
            // Use it as an acknowledgement, then reload chart data from dashboard endpoint.
            const syncStatus = String(syncResponse?.status || '').toUpperCase();
            if (syncStatus && syncStatus !== 'SUCCESS') {
                throw new Error(syncResponse?.message || 'Sync request failed');
            }

            showSnackbar({ message: successMessage, severity: "info" });
            await fetchDashboardData(key);
        } catch (error) {
            showSnackbar({ message: errorMessage, severity: "error" });
        }
    };

    const handleDeliverablesRefresh = () => {
        refreshISheetData(
            HQISheetName.DELIVERABLES,
            SnackbarMessage.FAILED_TO_SYNC_Deliverables,
            SnackbarMessage.SYNC_CUSTOM_VISUALIZATION_SUCCESS,
            "deliverables"
        )
    }

    const handleK1Refresh = () => {
        refreshISheetData(
            HQISheetName.K1_TRACKER,
            SnackbarMessage.FAILED_TO_SYNC_K1_TRACKER,
            SnackbarMessage.SYNC_CUSTOM_VISUALIZATION_SUCCESS,
            "k1Tracker"
        );
    }

    const handleESignatureRefresh = () => {
        refreshISheetData(
            HQISheetName.DOCUMENT_METADATA,
            SnackbarMessage.FAILED_TO_SYNC_Document_Metadata,
            SnackbarMessage.SYNC_CUSTOM_VISUALIZATION_SUCCESS,
            "eSignature"
        );
    }

    const handleIRRefresh = () => {
        refreshISheetData(
            HQISheetName.INFORMATION_REQUEST,
            SnackbarMessage.FAILED_TO_SYNC_INFORMATION_REQUEST_LIST,
            SnackbarMessage.SYNC_CUSTOM_VISUALIZATION_SUCCESS,
            "ir"
        );
    }

    return (
        <div className="app-container">
            {
                isLoading ?
                    <div style={{ height: `calc(100vh - 280px)`, display: 'flex', justifyContent: "center", alignItems: "center" }}>
                        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                            <CircularProgress />
                        </Box>
                    </div>
                    :
                    <>

                        {/* Row 1: Two Bar Charts Side by Side */}
                        <div className="chart-row">
                            <div className="chart-container">
                                <K1StatusByFundEstimatedChart
                                    fundData={dashboardData.k1Tracker}
                                    onRefresh={handleK1Refresh}
                                    siteId={siteID}
                                    lastRefreshTime={k1LastRefreshTime}
                                />
                            </div>
                            <div className="chart-container">
                                <K1StatusByFundFinalizedChart
                                    fundData={dashboardData.k1Tracker}
                                    onRefresh={handleK1Refresh}
                                    siteId={siteID}
                                    lastRefreshTime={k1LastRefreshTime}
                                />
                            </div>
                        </div>

                        {fsSiteFlag && (
                            <>
                                {/* Row 2: Two Charts Side by Side */}

                                <div className="chart-row">
                                    <div className="chart-container">
                                        <FormStatusByEntityChart
                                            lastRefreshTime={lastRefreshTime}
                                            rawDeliverablesData={dashboardData.deliverables}
                                            siteId={siteID}
                                            onRefresh={handleDeliverablesRefresh}
                                            title="Deliverables - Status by Form" />
                                    </div>

                                    <div className="chart-container">
                                        <StatusbyDueDate
                                            rawDeliverablesData={dashboardData.deliverables}
                                            siteId={siteID}
                                            lastRefreshTime={lastRefreshTime}
                                            onRefresh={handleDeliverablesRefresh}
                                            title="Deliverables - Status by Due Date" />
                                    </div>
                                </div>

                                {/* Row 3: Two Charts Side by Side */}
                                <div className="chart-row">
                                    <div className="chart-container">
                                        <FundStatusChart
                                            rawDeliverablesData={dashboardData.deliverables}
                                            siteId={siteID}
                                            lastRefreshTime={lastRefreshTime}
                                            onRefresh={handleDeliverablesRefresh}
                                            title="Deliverables - Status by Fund"
                                        />
                                    </div>
                                    <div className="chart-container">
                                        <Deliverables_Status_Before_915
                                            rawDeliverablesData={dashboardData.deliverables}
                                            siteId={siteID}
                                            lastRefreshTime={lastRefreshTime}
                                            onRefresh={handleDeliverablesRefresh}
                                            title="Deliverables Status - Due before 9/15"
                                        />
                                    </div>
                                </div>

                                {/* Row 4: Two Charts Side by Side */}
                                <div className="chart-row">
                                    <div className="chart-container">
                                        <Deliverables_Status_after_915
                                            rawDeliverablesData={dashboardData.deliverables}
                                            siteId={siteID}
                                            lastRefreshTime={lastRefreshTime}
                                            onRefresh={handleDeliverablesRefresh}
                                            title="Deliverables Status - Overview (After 9/15)"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Row 5: Information Request Charts */}
                        <div className="chart-row">
                            <div className="chart-container">
                                <IRStatusChart rawIRData={dashboardData.ir} handleRefresh={handleIRRefresh} siteId={siteID} />
                            </div>

                            <div className="chart-container">
                                <ESignatureStatusChart rawESignatureData={dashboardData.eSignature} handleRefresh={handleESignatureRefresh} siteId={siteID} />
                            </div>
                        </div>
                    </>
            }


        </div>
    );
}

export default App;


